import { useId } from "react"; // Import useState for handling new option
import { option, OptionGroup } from "../../types/ProductType";
import ProductGroupName from "./ProductGroupName";
import ProductGroupOptionName from "./ProductGroupOptionName";
import { FormikErrors } from "formik";
import { MdOutlineDelete } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { OptionPriceInput } from "./OptionPriceInput";

type ProductOptionGroupProps = OptionGroup & {
  onChange: (updatedGroup: OptionGroup) => void;
  errorMustFillOneOption: string;
  errorNameAR: string;
  errorNameEN: string;
  errorLimit: string;
  errorOptions: FormikErrors<option>[];
  onDeleteGroup: (index: number) => void;
  onDeleteOption: (groupIndex: number, index: number) => void;
  groupIndex: number;
  options?: option[];
};

function ProductOptionGroup({
  limit,
  nameAR,
  nameEN,
  options,
  onChange,
  errorMustFillOneOption,
  errorNameAR,
  errorNameEN,
  errorLimit,
  errorOptions,
  onDeleteGroup,
  onDeleteOption,
  groupIndex,
}: ProductOptionGroupProps) {
  const { t } = useTranslation();
  const id = useId();

  // Handle group name change
  const handleGroupNameChange = (newValues: { ar: string; en: string }) => {
    onChange({
      limit,
      nameAR: newValues.ar,
      nameEN: newValues.en,
      options,
    });
  };

  // Handle limit change
  const handleLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      limit: +e.target.value,
      nameAR,
      nameEN,
      options,
    });
  };

  // Handle option price or name changes
  // const handleOptionChange = (index: number, newOption: option) => {
  //   const updatedOptions = [...options];
  //   updatedOptions[index] = newOption;
  //   onChange({
  //     limit,
  //     nameAR,
  //     nameEN,
  //     options: updatedOptions,
  //   });
  // };
  const handleOptionChange = (index: number, newOption: option) => {
    const updatedOptions = [...options];
    updatedOptions[index] = {
      ...newOption,
      price: newOption.price === undefined || newOption.price === null ? undefined : newOption.price, // Handle undefined or null
    };
    onChange({
      limit,
      nameAR,
      nameEN,
      options: updatedOptions,
    });
  };

  // Handle adding new option
  const handleAddOption = () => {
    onChange({
      limit,
      nameAR,
      nameEN,
      options: [{ nameAR: "", nameEN: "", price: undefined },...options],
    });
  };

  return (
    <div className="min-h-24 space-y-2 rounded-lg border border-gray-400 px-4 py-3">
      <div className="flex flex-col gap-2">
        {/* Group Name */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold capitalize text-gray-700">
            {t("productOptionGroup.groupName")}
          </label>
          <div className="flex items-center gap-2">
            <ProductGroupName
              productGroupNames={{ ar: nameAR, en: nameEN }}
              onChange={handleGroupNameChange}
            />
            <button
              onClick={() => onDeleteGroup(groupIndex)}
              type="button"
              className="self-end rounded bg-red-500 px-4 py-2 font-bold text-white hover:bg-red-700"
            >
              <MdOutlineDelete size={20} />
            </button>
          </div>
          {errorNameAR && <p className="text-xs text-red-500">{errorNameAR}</p>}
          {errorNameEN && <p className="text-xs text-red-500">{errorNameEN}</p>}
        </div>

        {/* Limit and Add Option Button */}
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <label
              className="text-sm font-semibold capitalize text-gray-700"
              htmlFor={`${id}-limit`}
            >
              {t("productOptionGroup.limit")}
            </label>
            <div className="flex flex-col gap-2">
              <input
                value={limit}
                className="h-8 max-w-24 rounded-md border border-gray-400 text-center"
                type="number"
                name={`${id}-limit`}
                id={`${id}-limit`}
                onChange={handleLimitChange}
              />
              {errorLimit && (
                <p className="text-xs text-red-500">{errorLimit}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            className="self-end rounded-md bg-sidebar px-2 py-[6px] text-skyblue"
            onClick={handleAddOption} // Add new option field
          >
              {t("productOptionGroup.addItem")}
              </button>
        </div>

        {/* Options */}
        <div className="mt-2 flex w-full flex-col gap-3">
          {options.length > 0 && (
            <h3 className="text-lg font-semibold">
              {t("productOptionGroup.options")}
              </h3>
          )}
          {options.map((option, i) => (
            <div
              key={i}
              className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-300 p-3 sm:flex-nowrap"
            >
              {/* Option Name */}
              <div className="flex flex-1 flex-col gap-2 self-start">
                <label className="text-sm font-semibold capitalize text-gray-700">
                {t("productOptionGroup.itemName")}
                </label>
                <ProductGroupOptionName
                  productGroupOptionNames={{
                    ar: option.nameAR,
                    en: option.nameEN,
                  }}
                  onChange={(newValues) =>
                    handleOptionChange(i, {
                      ...option,
                      nameAR: newValues.ar,
                      nameEN: newValues.en,
                    })
                  }
                />
                {errorOptions[i]?.nameAR && (
                  <p className="text-xs text-red-500">
                    {errorOptions[i].nameAR}
                  </p>
                )}
                {errorOptions[i]?.nameEN && (
                  <p className="text-xs text-red-500">
                    {errorOptions[i].nameEN}
                  </p>
                )}
              </div>

              {/* Option Price */}
              <OptionPriceInput
              value={option.price}
              onDelete={() => onDeleteOption(groupIndex, i)}
              onChange={(price) => handleOptionChange(i, { ...option, price })}
              error={errorOptions[i]?.price}
              label={t("productOptionGroup.itemPrice")}
              placeholder={t("productOptionGroup.price") as string}
            />
            </div>
          ))}
        </div>
      </div>
      {errorMustFillOneOption && (
        <p className="text-xs text-red-500">{errorMustFillOneOption}</p>
      )}
    </div>
  );
}

export default ProductOptionGroup;
